/**
 * Result of extracting entity IDs from text/HTML description.
 */
export interface IExtractedEntityMentions {
  contactIds: string[];
  groupIds: string[];
  mediaIds: string[];
  memoryIds: string[];
  placeIds: string[];
}

/**
 * Extracts referenced Contact, Group, Media, Memory, and Place IDs from description HTML or text.
 * Matches semantic HTML tags:
 * - `<contact id="...">...</contact>`
 * - `<group id="...">...</group>`
 * - `<media id="...">...</media>`
 * - `<memory id="...">...</memory>`
 * - `<place id="...">...</place>`
 * And attributes like `data-contact-id`, `data-group-id`, `data-media-id`, `data-memory-id`, `data-place-id`.
 *
 * @param {string} [html] - The HTML or text content to parse.
 * @returns {IExtractedEntityMentions} Lists of distinct extracted IDs per entity type.
 */
export function extractEntityMentionsFromHtml(html?: string | null): IExtractedEntityMentions {
  const result: IExtractedEntityMentions = {
    contactIds: [],
    groupIds: [],
    mediaIds: [],
    memoryIds: [],
    placeIds: [],
  };

  if (!html || typeof html !== "string" || !html.trim()) {
    return result;
  }

  const contactSet = new Set<string>();
  const groupSet = new Set<string>();
  const mediaSet = new Set<string>();
  const memorySet = new Set<string>();
  const placeSet = new Set<string>();

  // 1. Match semantic entity tags: <(contact|group|media|memory|place) id="...">
  const entityTagRegex = /<(contact|group|media|memory|place)\s+[^>]*?id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = entityTagRegex.exec(html)) !== null) {
    const type = match[1].toLowerCase();
    const id = (match[2] ?? match[3] ?? match[4] ?? "").trim();
    if (!id) continue;

    if (type === "contact") contactSet.add(id);
    else if (type === "group") groupSet.add(id);
    else if (type === "media") mediaSet.add(id);
    else if (type === "memory") memorySet.add(id);
    else if (type === "place") placeSet.add(id);
  }

  // 2. Match data attributes: data-(contact|group|media|memory|place)-id="..."
  const dataAttrRegex = /data-(contact|group|media|memory|place)-id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;
  while ((match = dataAttrRegex.exec(html)) !== null) {
    const type = match[1].toLowerCase();
    const id = (match[2] ?? match[3] ?? match[4] ?? "").trim();
    if (!id) continue;

    if (type === "contact") contactSet.add(id);
    else if (type === "group") groupSet.add(id);
    else if (type === "media") mediaSet.add(id);
    else if (type === "memory") memorySet.add(id);
    else if (type === "place") placeSet.add(id);
  }

  result.contactIds = Array.from(contactSet);
  result.groupIds = Array.from(groupSet);
  result.mediaIds = Array.from(mediaSet);
  result.memoryIds = Array.from(memorySet);
  result.placeIds = Array.from(placeSet);

  return result;
}

export const extractEntityMentions = extractEntityMentionsFromHtml;
